import { supabase } from '../lib/supabase';

// Function to replace all vmon.vn image URLs in content with Supabase Storage URLs
export const migrateContentImages = async (
  content: string,
  progressCallback?: (current: number, total: number) => void
): Promise<{ updatedContent: string, replacedUrls: { original: string, new: string }[] }> => {
  if (!content) return { updatedContent: content, replacedUrls: [] };
  
  // Regular expression to find image tags with vmon.vn URLs
  const imgRegex = /<img[^>]+src="([^"]+vmon\.vn[^"]+)"[^>]*>/g;
  
  let match;
  let updatedContent = content;
  const replacedUrls: { original: string, new: string }[] = [];
  const foundUrls: string[] = [];
  
  // Find all vmon.vn image URLs
  while ((match = imgRegex.exec(content)) !== null) {
    const fullTag = match[0];
    const srcUrl = match[1];
    
    // Skip if already a Supabase URL or already processed
    if (srcUrl.includes('images.vmon.vn') || foundUrls.includes(srcUrl)) continue;
    
    foundUrls.push(srcUrl);
  }
  
  // Process each unique URL
  for (let i = 0; i < foundUrls.length; i++) {
    const url = foundUrls[i];
    
    try {
      // Extract path from URL
      let path = '';
      try {
        const urlObj = new URL(url);
        path = urlObj.pathname;
      } catch {
        // If URL parsing fails, use the original URL
        path = url.replace('https://vmon.vn', '');
      }
      
      // Clean up path
      path = path.startsWith('/') ? path.substring(1) : path;
      
      // Add timestamp to ensure uniqueness
      const timestamp = Date.now();
      const parts = path.split('.');
      const extension = parts.pop() || 'jpg';
      const name = parts.join('.');
      const newPath = `wp-content/uploads/${name}-${timestamp}.${extension}`;
      
      // Download the image
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      
      // Upload to Supabase
      const { data, error } = await supabase.storage
        .from('vmon')
        .upload(newPath, blob, {
          cacheControl: '3600',
          upsert: true
        });
        
      if (error) throw error;
      
      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('vmon')
        .getPublicUrl(newPath);
      
      // Replace all occurrences in content
      updatedContent = updatedContent.replace(new RegExp(escapeRegExp(url), 'g'), publicUrl);
      
      // Add to replaced URLs
      replacedUrls.push({ original: url, new: publicUrl });
      
      // Update progress
      if (progressCallback) {
        progressCallback(i + 1, foundUrls.length);
      }
    } catch (error) {
      console.error('Error processing image:', url, error);
    }
  }
  
  return { updatedContent, replacedUrls };
};

// Function to replace featured image URL
export const migrateFeaturedImage = async (
  imageUrl: string
): Promise<string> => {
  if (!imageUrl) return imageUrl;
  
  // Skip if already a Supabase URL
  if (imageUrl.includes('images.vmon.vn')) return imageUrl;
  
  // Skip if not a vmon.vn URL
  if (!imageUrl.includes('vmon.vn')) return imageUrl;
  
  try {
    // Extract path from URL
    let path = '';
    try {
      const urlObj = new URL(imageUrl);
      path = urlObj.pathname;
    } catch {
      // If URL parsing fails, use the original URL
      path = imageUrl.replace('https://vmon.vn', '');
    }
    
    // Clean up path
    path = path.startsWith('/') ? path.substring(1) : path;
    
    // Add timestamp to ensure uniqueness
    const timestamp = Date.now();
    const parts = path.split('.');
    const extension = parts.pop() || 'jpg';
    const name = parts.join('.');
    const newPath = `wp-content/uploads/${name}-${timestamp}.${extension}`;
    
    // Download the image
    const response = await fetch(imageUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`);
    }
    
    const blob = await response.blob();
    
    // Upload to Supabase
    const { data, error } = await supabase.storage
      .from('vmon')
      .upload(newPath, blob, {
        cacheControl: '3600',
        upsert: true
      });
      
    if (error) throw error;
    
    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from('vmon')
      .getPublicUrl(newPath);
    
    return publicUrl;
  } catch (error) {
    console.error('Error processing featured image:', imageUrl, error);
    return imageUrl;
  }
};

// Helper function to escape special characters in regex
const escapeRegExp = (string: string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};